import { get } from 'lodash';
import { DbBasic } from '../types/nestedKey.type';
import { Collection } from '../types/database.type';
import { NullableArr, Nullish } from '../types/typeish.type';
import { ObjHlp } from './object.helper';

/**
 * Various functions to manipulate arrays
 */
export class ArrHlp {
  /**
   * Transform singe object, array or null to array.
   * @param {NullableArr<T>} x
   * @return {T[]}
   */
  public static make<T>(x: NullableArr<T>): T[] {
    return x == null ? [] : Array.isArray(x) ? x : [x];
  }

  /**
   * Generate series of numbers
   * @param {number} start first element of series
   * @param {number} end   upper limit of series (inclusive)
   * @param {number} step  difference between elements - default 1
   * @return {number[]}
   */
  public static series(start: number, end: number, step: number = 1): number[] {
    if (!step) throw new Error('Step value cannot be zero!');
    const arr = [];
    for (let i = start; i <= end; i += step) arr.push(i);
    return arr;
  }

  /**
   * returns array[index], truncating indexes to fit in bounds, minus indexes treated as reverse: -1 returns last and -length
   * first element
   * @param {NullableArr<T>} array
   * @param {number}         index
   * @return {T | undefined}
   */
  public static get<T>(array: NullableArr<T>, index: number): T | undefined {
    if (!Number.isInteger(index) || !array) {
      return undefined;
    }
    if (!Array.isArray(array)) return array;

    const size = array.length;
    if (index >= 0) {
      return index < size ? array[index] : array[size - 1];
    } else {
      return index >= -size ? array[size + index] : array[0];
    }
  }

  /**
   * Filter all duplicate object by given field path or comparator function
   * @param {T[]}               arr     table to duplicate
   * @param {string | function} compare path to compared field or comparator function
   * @return {T[]} filtered table without duplicates and empty values
   */
  public static filterDuplicates<T>(arr: T[], compare: string | ((x: T, y: T) => boolean)): T[] {
    const predicate =
      typeof compare === 'function'
        ? (x: T, i: number) => i == arr.findIndex((y) => compare(x, y))
        : (x: T, i: number) => i == arr.findIndex((y) => get(x, compare) == get(y, compare));

    return arr.filter(predicate);
  }

  /**
   * Filter all empty or duplicate object by given field path
   * @param {T[]}     arr               table to duplicate
   * @param {string}  path              path to compared field
   * @param {boolean} handleWhitespaces treats strings of all whitespaces as empty
   * @return {T[]} filtered table without duplicates and empty values
   */
  public static filterDuplicatesAndEmpty<T>(arr: T[], path: string, handleWhitespaces = false): T[] {
    const checkEmpty = handleWhitespaces ? (x: string) => x.trim() !== '' : (x: string) => x !== '';
    const getVal = (a: T) => (ObjHlp.isPrimitive(a) ? a : get(a, path));
    const predicate = (x: T, i: number) => {
      const xVal = getVal(x);
      return checkEmpty(xVal) && i == arr.findIndex((y) => xVal == getVal(y));
    };
    return arr.filter(predicate);
  }

  /**
   * Maps elements of collection to new instance of array
   * @param {T[]}      collection
   * @param {function} callback
   * @return {U[]}
   */
  public static map<T, U>(collection: T[], callback: (value: T, index: number, collection: T[]) => U): U[];

  /**
   * Maps elements of collection to new instance of array
   * @param {Set<T>}   collection
   * @param {function} callback
   * @return {U[]}
   */
  public static map<T, U>(collection: Set<T>, callback: (value: T, value_: T, collection: Set<T>) => U): U[];

  /**
   * Maps elements of collection to new instance of array
   * @param {Map<K, T>} collection
   * @param {function}  callback
   * @return {U[]}
   */
  public static map<T, U, K>(collection: Map<K, T>, callback: (value: T, key: K, collection: Map<K, T>) => U): U[];

  // Implementation
  // eslint-disable-next-line require-jsdoc
  public static map<T, U, K, C extends T[] | Map<K, T> | Set<T>>(collection: C, callback: (x: T, key: K, col: C) => U): U[] {
    const result: U[] = [];
    collection.forEach((x, key, col) => result.push(callback(x, key as K, col as C)));
    return result;
  }

  /**
   * Maps matching elements of collection to new instance of array
   * @param {T[]}      collection array to map
   * @param {function} predicate  filter predicate function
   * @param {function} callback   map callback function
   * @return {U[]} filtered and mapped array
   */
  public static filterMap<T, U>(
    collection: T[],
    predicate: (value: T, index: number, collection: T[]) => boolean,
    callback: (value: T, index: number, collection: T[]) => U
  ): U[];

  /**
   * Maps matching elements of collection to new instance of array
   * @param {Set<T>}   collection set to map
   * @param {function} predicate  filter predicate function
   * @param {function} callback   map callback function
   * @return {U[]} filtered and mapped array
   */
  public static filterMap<T, U>(
    collection: Set<T>,
    predicate: (value: T, value_: T, collection: Set<T>) => boolean,
    callback: (value: T, value_: T, collection: Set<T>) => U
  ): U[];

  /**
   * Maps matching elements of collection to new instance of array
   * @param {Map<K, T>} collection map to map
   * @param {function}  predicate  filter predicate function
   * @param {function}  callback   map callback function
   * @return {U[]} filtered and mapped array
   */
  public static filterMap<T, U, K>(
    collection: Map<K, T>,
    predicate: (value: T, key: K, collection: Map<K, T>) => boolean,
    callback: (value: T, key: K, collection: Map<K, T>) => U
  ): U[];

  // Implementation
  // eslint-disable-next-line require-jsdoc
  public static filterMap<T, U, K, C extends T[] | Map<K, T> | Set<T>>(
    collection: C,
    predicate: (x: T, key: K, collection: C) => boolean,
    callback: (x: T, key: K, collection: C) => U
  ): U[] {
    const result: U[] = [];
    collection.forEach((x, key, col) => {
      if (predicate(x, key as K, col as C)) result.push(callback(x, key as K, col as C));
    });
    return result;
  }

  /**
   * Check if arrays have equal length and every value is at the same index.
   * @param {T[] | Nullish} arr1
   * @param {T[] | Nullish} arr2
   * @return {boolean}
   */
  public static areEqual<T = DbBasic>(
    arr1: T[] | Nullish,
    arr2: T[] | Nullish
  ): boolean {
    if (arr1 === arr2) return true;
    if (!arr1 || !arr2 || arr1.length !== arr2.length) return false;
    return arr1.every((value, index) => value === arr2[index]);
  }

  /**
   * Checks how many objects are different in two arrays
   * @param {T[]}               arr1
   * @param {T[]}               arr2
   * @param {string | function} comparator path to compared field or comparator function
   * @return {number} number of different objects from arr1 perspective
   */
  public static checkDifferences<T>(arr1: T[], arr2: T[], comparator: string | ((x: T, y: T) => boolean)): number {
    const predicate =
      typeof comparator === 'function'
        ? (x: T) => arr2.findIndex((y) => comparator(x, y)) == -1
        : (x: T) => arr2.findIndex((y) => get(x, comparator) == get(y, comparator)) == -1;

    return arr1.filter(predicate).length;
  }

  /**
   * Insert element to place given by index
   * @param {T[]}    arr
   * @param {T}      elem  element to add
   * @param {number} index selected index; if it's less than 0, 0 is taken, if it's more than length - 1, length - 1 i taken
   * @return {T[]} array with added elem in place
   */
  public static insert<T>(arr: T[], elem: T, index: number): T[] {
    if (index <= 0) return [elem, ...arr];
    if (index >= arr.length) return [...arr, elem];

    const left = arr.slice(0, index);
    const right = arr.slice(index);
    return [...left, elem, ...right];
  }

  /**
   * Returns copy of an array with first given element removed
   * @param {T[]}      array
   * @param {function} predicate
   * @return {T[]} copy
   */
  public static removeFirstMatch<T>(array: T[], predicate: (v: T, i: number, a: T[]) => boolean): T[] {
    const copy = [...array];

    const index = array.findIndex(predicate);
    if (index >= 0) copy.splice(index, 1);

    return copy;
  }

  /**
   * Remove all matches from copy of array
   * @param {T[]}      array
   * @param {function} predicate
   * @return {T[]} copy with removed matched values
   */
  public static removeAllMatches<T>(array: T[], predicate: (v: T, i: number, a: T[]) => boolean): T[] {
    const copy = [...array];

    for (let i = array.length - 1; i >= 0; i--) {
      if (predicate(array[i], i, array)) copy.splice(i, 1);
    }

    return copy;
  }

  /**
   * Merge multiple same-type arrays into one
   * @param {T[]} arrays
   * @return {T[]}
   */
  public static merge<T>(...arrays: (T[] | Nullish)[]): T[] {
    const ret: T[] = [];
    arrays.forEach((arr) => {
      if (arr && Array.isArray(arr)) {
        ret.push(...arr);
      }
    });
    return ret;
  }

  /**
   * Make array from object values or return empty array if null or undefined given
   * @param {Collection<T> | Nullish} obj
   * @return {T[]}
   */
  public static fromObj<T>(obj: Collection<T> | Nullish): T[] {
    return obj ? Object.values(obj) : [];
  }

  /**
   * Returns the intersection of two arrays - an array of elements that are present in both arr1 and arr2
   * @param {T[]} arr1
   * @param {T[]} arr2
   * @return {T[]} array of common elements in arr1 and arr2
   */
  public static intersection<T>(arr1: T[], arr2: T[]): T[] {
    const set2 = new Set(arr2);
    const result: T[] = [];
    for (const elem of arr1) {
      if (set2.has(elem)) {
        result.push(elem);
        set2.delete(elem); // Remove the element to avoid duplicates in the result
      }
    }
    return result;
  }

  // eslint-disable-next-line valid-jsdoc
  /**
   * Split array into smaller chunks with given size
   * @param {T[]}    arr
   * @param {number} size size of chunk, must be larger than 0
   * @return {T[][]} array of arrays, where inside are chunks of given array
   */
  public static chunk<T>(arr: T[], size: number): T[][] {
    if (size <= 0 || arr.length == 0) return [arr];
    const ret: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      ret.push(arr.slice(i, i + size));
    }
    return ret;
  }
}
